import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Inbox, RefreshCw, Loader2 } from "lucide-react";
import { useAuth, http } from "@/lib/auth";
import { Logo } from "@/components/site/Logo";
import { InquiryDetail } from "./InquiryDetail";
import AdminVehicles from "./AdminVehicles";
import { INTEREST_LABELS, STATUS_STYLES, fmtDate } from "./adminShared";

const FILTERS = ["all", "new", "contacted", "closed"];

const Stat = ({ id, label, value }) => (
  <div data-testid={`admin-stat-${id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-2 font-display text-3xl font-extrabold text-[#0A2540]">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("inquiries");

  const load = () => http.get("/admin/inquiries").then((r) => setItems(r.data)).catch(() => toast.error("Could not load inquiries"));
  useEffect(() => { if (user) load(); }, [user]);

  const shown = useMemo(() => (items || []).filter((i) =>
    (filter === "all" || i.status === filter) &&
    [i.name, i.company, i.email, i.country, i.message].join(" ").toLowerCase().includes(q.toLowerCase())
  ), [items, q, filter]);

  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A2540]" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const replace = (upd) => {
    setItems((list) => list.map((i) => (i.id === upd.id ? upd : i)));
    setSelected(upd);
  };
  const onStatus = async (inq, status) => {
    try {
      replace((await http.patch(`/admin/inquiries/${inq.id}`, { status })).data);
    } catch {
      toast.error("Could not update status");
    }
  };
  const onDelete = async (inq) => {
    if (!window.confirm(`Delete inquiry from ${inq.company}?`)) return;
    try {
      await http.delete(`/admin/inquiries/${inq.id}`);
    } catch {
      return toast.error("Could not delete inquiry");
    }
    setItems((list) => list.filter((i) => i.id !== inq.id));
    setSelected(null);
    toast.success("Inquiry deleted");
  };
  const count = (s) => (items || []).filter((i) => i.status === s).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC] text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-4"><Logo onLight /><span className="hidden rounded-full bg-[#0A2540] px-3 py-1 text-xs font-bold uppercase tracking-wider text-white sm:inline">Admin</span></div>
          <div className="flex items-center gap-4">
            <span data-testid="admin-user-email" className="hidden text-sm text-slate-500 sm:inline">{user.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-[#0A2540] hover:bg-slate-50"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <div className="mb-8 inline-flex rounded-full bg-white p-1 ring-1 ring-slate-200">
          {["inquiries", "vehicles"].map((k) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-5 py-2 text-sm font-bold capitalize transition-colors ${tab === k ? "bg-[#0A2540] text-white" : "text-[#0A2540] hover:bg-slate-100"}`}>{k}</button>
          ))}
        </div>
        {tab === "vehicles" ? <AdminVehicles /> : <>
        <h1 className="font-display text-3xl font-bold text-[#0A2540] sm:text-4xl">Inquiries</h1>
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat id="total" label="Total" value={items?.length ?? "–"} />
          <Stat id="new" label="New" value={count("new")} />
          <Stat id="contacted" label="Contacted" value={count("contacted")} />
          <Stat id="closed" label="Closed" value={count("closed")} />
        </div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-sm font-bold capitalize transition-colors ${filter === f ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540] hover:bg-slate-100"}`}>{f}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <label className="relative block sm:w-72">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…" className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-[#0A2540]" />
            </label>
            <button data-testid="admin-refresh-button" onClick={load} aria-label="Refresh" className="rounded-full border border-slate-200 bg-white p-2.5 text-[#0A2540] hover:bg-slate-50"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {items === null ? (
            <div className="grid h-48 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : shown.length === 0 ? (
            <div data-testid="admin-empty" className="flex h-48 flex-col items-center justify-center gap-2 text-slate-500"><Inbox className="h-8 w-8" />No inquiries found</div>
          ) : (
            <div className="overflow-x-auto">
              <table data-testid="admin-inquiries-table" className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr><th className="px-5 py-3">Received</th><th className="px-5 py-3">Company / Contact</th><th className="px-5 py-3">Interest</th><th className="px-5 py-3">Country</th><th className="px-5 py-3">Message</th><th className="px-5 py-3">Status</th></tr>
                </thead>
                <tbody>
                  {shown.map((i) => (
                    <tr key={i.id} data-testid={`admin-inquiry-row-${i.id}`} onClick={() => setSelected(i)} className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-[#EBF3FF]/60">
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">{fmtDate(i.created_at)}</td>
                      <td className="px-5 py-4"><div className="font-bold text-[#0A2540]">{i.company}</div><div className="text-slate-500">{i.name} · {i.email}</div></td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-700">{INTEREST_LABELS[i.interest]}{i.vehicle_label && <div data-testid="admin-inquiry-vehicle" className="text-xs font-semibold text-[#2F7BFF]">{i.vehicle_label}</div>}</td>
                      <td className="px-5 py-4 text-slate-700">{i.country || "—"}</td>
                      <td className="max-w-xs truncate px-5 py-4 text-slate-500">{i.message}</td>
                      <td className="px-5 py-4"><span className={`rounded-full border px-3 py-1 text-xs font-bold capitalize ${STATUS_STYLES[i.status]}`}>{i.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>}
      </main>
      <InquiryDetail inquiry={selected} onClose={() => setSelected(null)} onStatus={onStatus} onDelete={onDelete} />
    </div>
  );
}
